import { useState, useEffect, useContext } from "react";
import axios from "axios";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Rocket, Buildings, ArrowRight, Warning, CheckCircle, SpinnerGap } from "@phosphor-icons/react";
import { AuthContext } from "../contexts/AuthContext";
import { canAccessAdmin } from "../utils/permissions";
import { toast } from "sonner";
import { useLang } from "../contexts/LanguageContext";

import { API_BASE as API } from "../lib/apiBase";

/**
 * PromoteVideoButton
 * Lets an admin/founder promote (clone) a video — and all its data —
 * from the current sandbox org to another target org (typically production FLHQ).
 *
 * Use case: Knoria sandbox → Finance Libre HQ (release V1)
 */
export default function PromoteVideoButton({ video, onPromoted }) {
  const { user } = useContext(AuthContext);
  // Tâche 1358 (Eric 2026-06-30) — Cette fenêtre restait entièrement en
  // français en version anglaise.
  const { tr } = useLang();
  const [open, setOpen] = useState(false);
  const [orgs, setOrgs] = useState([]);
  const [targetOrgId, setTargetOrgId] = useState("");
  const [note, setNote] = useState("");
  const [promoting, setPromoting] = useState(false);
  const [result, setResult] = useState(null);

  // Tâche 1136 (Eric 2026-06-09) — Le bouton était INVISIBLE pour les
  // fondateurs : la garde se fiait à `user.memberships`, que /api/auth/me
  // renvoie vide pour eux (ils accèdent à toutes les organisations sans
  // enregistrement d'adhésion). La liste doit venir de /api/organizations/my,
  // chargée au montage — pas à l'ouverture, sinon on ne sait pas s'il faut
  // afficher le bouton.
  useEffect(() => {
    if (!canAccessAdmin(user)) return;
    axios
      .get(`${API}/api/organizations/my`, { withCredentials: true })
      .then((r) => {
        const others = (r.data.organizations || []).filter((o) => o.id !== user?.org_id);
        setOrgs(others);
        if (others.length > 0) setTargetOrgId(others[0].id);
      })
      .catch(() => setOrgs([]));
  }, [user, user?.org_id]);

  const canPromote = canAccessAdmin(user) && orgs.length > 0;
  if (!canPromote) return null;

  const handlePromote = async () => {
    if (!targetOrgId) {
      toast.error(tr("Choisissez une organisation cible", "Pick a target organisation"));
      return;
    }
    setPromoting(true);
    try {
      const r = await axios.post(
        `${API}/api/videos/${video.id}/promote`,
        { target_org_id: targetOrgId, note },
        { withCredentials: true }
      );
      setResult(r.data);
      toast.success(tr(`Vidéo promue vers ${r.data.target_org_name}`, `Video promoted to ${r.data.target_org_name}`));
      if (onPromoted) onPromoted(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || tr("Erreur de promotion", "Promotion failed"));
    } finally {
      setPromoting(false);
    }
  };

  const reset = () => {
    setOpen(false);
    setResult(null);
    setNote("");
    setTargetOrgId("");
  };

  return (
    <>
      <Button
        onClick={() => setOpen(true)}
        variant="outline"
        size="sm"
        className="gap-2 border-teal-500/40 text-teal-700 hover:bg-teal-50 dark:hover:bg-teal-950/30 dark:text-teal-300"
        data-testid="btn-promote-video"
      >
        <Rocket size={16} />
        {tr("Promouvoir", "Promote")}
      </Button>

      <Dialog open={open} onOpenChange={(v) => (v ? setOpen(true) : reset())}>
        <DialogContent className="max-w-lg" data-testid="dialog-promote-video">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Rocket size={20} className="text-teal-600" />
              {tr("Promouvoir cette vidéo", "Promote this video")}
            </DialogTitle>
            <DialogDescription>
              {tr("Cloner la vidéo et toutes ses données (transcriptions, Q&R, locuteurs) vers une autre organisation.", "Clone the video and all its data (transcripts, Q&A, speakers) into another organisation.")}
            </DialogDescription>
          </DialogHeader>

          {!result ? (
            <div className="space-y-5 py-3">
              {/* Source */}
              <div className="flex items-center gap-3 p-3 bg-secondary/40 rounded-lg">
                <Buildings size={20} className="text-muted-foreground" />
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">Source</div>
                  <div className="font-semibold truncate">{video.title}</div>
                  <div className="text-xs text-muted-foreground">
                    {tr("Org actuelle (vous êtes ici)", "Current org (you are here)")}
                  </div>
                </div>
              </div>

              <div className="flex justify-center">
                <ArrowRight size={20} className="text-muted-foreground" />
              </div>

              {/* Target */}
              <div>
                <label className="block text-sm font-semibold mb-2">{tr("Organisation cible", "Target organisation")}</label>
                {orgs.length === 0 ? (
                  <div className="p-3 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 rounded-lg text-sm">
                    <Warning size={16} className="inline mr-2 text-amber-600" />
                    {tr("Vous n'êtes membre d'aucune autre organisation.", "You are not a member of any other organisation.")}
                  </div>
                ) : (
                  <select
                    value={targetOrgId}
                    onChange={(e) => setTargetOrgId(e.target.value)}
                    className="w-full px-3 py-2 bg-card border border-border rounded-lg focus:ring-2 focus:ring-[#002FA7] focus:border-transparent"
                    data-testid="select-target-org"
                  >
                    {orgs.map((o) => (
                      <option key={o.id} value={o.id}>
                        {`${o.name} ${o.is_platform ? "(plateforme)" : ""} · ${o.plan || "—"}`}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              {/* Release note */}
              <div>
                <label className="block text-sm font-semibold mb-2">
                  {tr("Note de version", "Release note")} <span className="text-muted-foreground text-xs">{tr("(optionnel)", "(optional)")}</span>
                </label>
                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  rows={2}
                  placeholder={tr("ex : Version validée par Vincent et Etienne, 25 avr 2026", "e.g. Version approved by Vincent and Etienne, 25 Apr 2026")}
                  className="w-full px-3 py-2 bg-card border border-border rounded-lg focus:ring-2 focus:ring-[#002FA7] focus:border-transparent text-sm"
                  data-testid="input-promotion-note"
                />
              </div>

              {/* Warning */}
              <div className="p-3 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg text-xs text-blue-800 dark:text-blue-200">
                <Warning size={14} className="inline mr-1.5 text-blue-600" />
                {tr("Cette action clone la vidéo : le fichier reste partagé en stockage, mais une nouvelle entrée est créée dans l'organisation cible avec ses transcriptions et ses Q&R.", "This action clones the video: the file stays shared in storage, but a new entry is created in the target organisation with its transcripts and Q&A.")}
              </div>

              <div className="flex gap-2 justify-end pt-2">
                <Button variant="outline" onClick={reset} disabled={promoting} data-testid="btn-cancel-promote">
                  {tr("Annuler", "Cancel")}
                </Button>
                <Button
                  onClick={handlePromote}
                  disabled={promoting || !targetOrgId || orgs.length === 0}
                  className="bg-teal-600 hover:bg-teal-700 text-white"
                  data-testid="btn-confirm-promote"
                >
                  {promoting ? (
                    <>
                      <SpinnerGap size={16} className="animate-spin mr-2" />
                      {tr("Promotion…", "Promoting…")}
                    </>
                  ) : (
                    <>
                      <Rocket size={16} className="mr-2" />
                      {tr("Promouvoir", "Promote")}
                    </>
                  )}
                </Button>
              </div>
            </div>
          ) : (
            <div className="py-3" data-testid="promotion-result">
              <div className="flex flex-col items-center text-center mb-4">
                <div className="w-14 h-14 rounded-full bg-teal-100 dark:bg-teal-950/40 flex items-center justify-center mb-3">
                  <CheckCircle size={32} className="text-teal-600" />
                </div>
                <h3 className="text-lg font-bold mb-1">{tr("Promotion réussie !", "Promotion successful")}</h3>
                <p className="text-sm text-muted-foreground">
                  {tr("Vidéo clonée vers", "Video cloned to")} <strong>{result.target_org_name}</strong>
                </p>
              </div>

              <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="text-center p-3 bg-secondary/40 rounded-lg">
                  <div className="text-2xl font-bold text-[#002FA7]">{result.stats.knowledge_units}</div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{tr("Q&R clonées", "Q&A cloned")}</div>
                </div>
                <div className="text-center p-3 bg-secondary/40 rounded-lg">
                  <div className="text-2xl font-bold text-[#002FA7]">{result.stats.segments}</div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{tr("Segments transcrits", "Transcribed segments")}</div>
                </div>
              </div>

              <div className="text-xs text-muted-foreground bg-secondary/40 rounded-lg p-3 mb-4 font-mono">
                <div>{tr("ID nouvelle vidéo :", "New video ID:")} <span className="text-foreground">{result.new_video_id}</span></div>
                <div>Promotion ID : <span className="text-foreground">{result.promotion_id}</span></div>
              </div>

              <Button onClick={reset} className="w-full" data-testid="btn-close-promotion-result">
                {tr("Fermer", "Close")}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
